import React, { useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useBoolean } from 'react-hanger';
import DOMPurify from 'dompurify';

type Props = {
    contentHtml?: string;

    /**
     * Text length above which the content collapses to three lines and gains an expand control.
     * It decides *whether* to clamp, not where to cut — the cut itself is done by line with CSS, so
     * markup survives it.
     */
    maxLength?: number;

    /**
     * Emphasise `(parenthesised)`, `#123` and email segments — the parts that identify *which*
     * job this is: `[Auto][Live] Campaign Sync #14385 (Tesco Back to School 2026)`.
     *
     * `[bracketed]` tags are deliberately left plain. Nearly every row carries two of them, so
     * emphasising them put bold on the one part of the name that never distinguishes one row from
     * another, and the column ended up more bold than not.
     *
     * Content is rendered from the sanitised text rather than as HTML in this mode, so the
     * emphasis is built out of React nodes and never round-trips markup through the sanitiser.
     */
    highlight?: boolean;

    /**
     * Makes each emphasised segment a button that reports its own text. Only meaningful alongside
     * `highlight`, since the segments are what is clicked.
     */
    onSegmentClick?: (segment: string) => void;

    /** Tooltip for a clickable segment, given its text. */
    segmentTitle?: (segment: string) => string;

    /**
     * Where a segment should link to, if anywhere. A segment with a href renders as a real anchor
     * rather than a button, so middle-click, cmd-click and "open in new tab" all behave as the
     * reader expects and the target shows in the status bar on hover.
     */
    segmentHref?: (segment: string) => string | undefined;
};

const allowedTags = ['a', 'button', 'p', 'br', 'ul', 'li', 'b', 'i'];
const allowedAttr = ['href', 'title', 'target', 'type'];

/**
 * One source for both regexes below, so the splitter and the test can never drift apart.
 *
 * The email arm requires at least one dot after the `@` and only word characters after each dot,
 * so a sentence-ending period is left outside the match.
 */
const segmentSource = String.raw`\([^)]*\)|#[\w-]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+`;

// Split rather than match, so the text between segments is preserved (a capturing group makes
// String.split keep the separators).
const segmentSplitter = new RegExp(`(${segmentSource})`, 'g');

// Deliberately not /g — a global regex would carry lastIndex between test() calls.
const segmentMatcher = new RegExp(`^(?:${segmentSource})$`);

export const isSegment = (part: string) => segmentMatcher.test(part);

/** Exported for testing: the ordered parts of `text`, ready to be emphasised or left plain. */
export const splitHighlightSegments = (text: string): string[] =>
    text.split(segmentSplitter).filter(part => part.length > 0);

/**
 * A dotted underline and a pointer are the affordance: the segments sit mid-sentence inside a
 * table cell, so a link colour or a solid underline reads as though the row itself is a link, and
 * a button chrome would break the line up. Hover and keyboard focus share one treatment.
 */
const clickableSegmentSx = {
    font: 'inherit',
    color: 'inherit',
    p: 0,
    border: 0,
    background: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: '2px',
    borderRadius: 0.5,
    '&:hover, &:focus-visible': {
        bgcolor: 'action.hover',
        textDecorationStyle: 'solid',
    },
} as const;

const renderHighlighted = (
    text: string,
    onSegmentClick?: (segment: string) => void,
    segmentTitle?: (segment: string) => string,
    segmentHref?: (segment: string) => string | undefined,
) =>
    splitHighlightSegments(text).map((part, index) => {
        if (!isSegment(part)) return part;

        const key = `${index}-${part}`;
        const href = segmentHref?.(part);

        if (!onSegmentClick && href == null) {
            return <strong key={key}>{part}</strong>;
        }

        const segment =
            href != null ? (
                <Box
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={segmentTitle?.(part)}
                    sx={clickableSegmentSx}
                    // The row is clickable in its own right; following a link is not a row click.
                    onClick={event => event.stopPropagation()}
                >
                    {part}
                </Box>
            ) : (
                <Box
                    component="button"
                    type="button"
                    aria-label={segmentTitle?.(part)}
                    sx={clickableSegmentSx}
                    onClick={event => {
                        // The row is clickable in its own right; a segment click is not a row click.
                        event.stopPropagation();
                        onSegmentClick?.(part);
                    }}
                >
                    {part}
                </Box>
            );

        const title = segmentTitle?.(part);

        // Tooltip rather than a title attribute, to match the rest of the table's affordances.
        return title ? (
            <Tooltip key={key} title={title}>
                {segment}
            </Tooltip>
        ) : (
            <React.Fragment key={key}>{segment}</React.Fragment>
        );
    });

const ExpandableHtmlContent: React.FC<Props> = ({
    contentHtml,
    maxLength = 200,
    highlight = false,
    onSegmentClick,
    segmentTitle,
    segmentHref,
}) => {
    const expand = useBoolean(false);

    // This runs for every cell of every row, and the table re-renders on each poll, so keep the
    // DOM work and both sanitiser passes behind a memo.
    const { plainText, shouldShorten } = useMemo(() => {
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(contentHtml ?? '');
        const textContent = div.textContent || div.innerText || '';

        return { plainText: textContent, shouldShorten: textContent.length > maxLength };
    }, [contentHtml, maxLength]);

    const clamped = shouldShorten && !expand.value;

    // The row is clickable in its own right; opening a note is not a row click.
    const toggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        expand.toggle();
    };

    /**
     * Sanitised in both states, not only when expanded.
     *
     * Cutting the *text* at a character count dropped every tag on the way, so a note built from
     * `<br>`-separated lines collapsed into one run — "…0 sent for import.Failed — not imported:".
     * Clamping by line with CSS keeps the markup intact, and because nothing is sliced there is no
     * longer any risk of cutting through the middle of a tag.
     */
    const html = useMemo(() => {
        if (highlight) return null;
        return DOMPurify.sanitize(contentHtml ?? '', {
            ALLOWED_TAGS: allowedTags,
            ALLOWED_ATTR: allowedAttr,
        });
    }, [contentHtml, highlight]);

    return (
        <Box display="flex" alignItems="flex-start" p={0.75}>
            <Box
                onClick={shouldShorten ? toggle : undefined}
                sx={{
                    // Several jobs separate lines with a literal newline rather than markup
                    // (ClassicPlayLogProcess, the Talon importers), so honour both.
                    whiteSpace: 'pre-line',
                    cursor: shouldShorten ? 'pointer' : undefined,
                    // The sanitiser strips style and class, so a note's own <ul> can only be
                    // styled from here. Browser defaults — 1em of vertical margin and a 40px
                    // indent — are far too loose for a dense table row, and the margin alone ate
                    // one of the three lines the clamp shows before anyone expands it.
                    '& ul': { margin: 0, paddingLeft: '1.25em' },
                    '& li': { marginTop: '0.15em' },
                    ...(clamped
                        ? {
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                          }
                        : {}),
                }}
            >
                {html == null ? (
                    <Typography component="span" fontSize="inherit">
                        {renderHighlighted(plainText, onSegmentClick, segmentTitle, segmentHref)}
                    </Typography>
                ) : (
                    <Box component="span" dangerouslySetInnerHTML={{ __html: html }} />
                )}
            </Box>
            {shouldShorten && (
                <Tooltip title={expand.value ? 'Collapse' : 'Expand'}>
                    <IconButton
                        onClick={toggle}
                        size="small"
                        aria-expanded={expand.value}
                        aria-label={expand.value ? 'Collapse' : 'Expand'}
                    >
                        {expand.value ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default ExpandableHtmlContent;
